import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';
import { createMoveInCosts, getIreneDashboard } from '@rental-manager/actions/requests';
import ConfirmMoveInCostsPage from './component';
import { DEFAULT_ERROR, MOVE_IN_COSTS_REQUEST_SUCCESS } from '@rental-manager/constants/NotificationConstants';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import type {
  MoveInCostsOnSubmitInterface,
  MoveInCostsSettingsInterface,
  ProratedRentSettingsInterface,
  SecurityDepositSettingsInterface,
} from '@rental-manager/pages/Dashboard/types';
import paths from '@rental-manager/pathsManifest';
import type { MoveInCostsInterface } from '@rental-manager/models/Types';
import { getMoveInCost } from '@common/paymentsUtils/moveInCostsUtil';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { propertyId, listing }) => {
  const { moveInCostsSettings, identityCheckResponse } = state.payments.landlord.onboarding[propertyId];
  const { rentTermsKey, tenantFirstName } =
    moveInCostsSettings.term === 'current'
      ? state.payments.landlord.property.currentTerm
      : state.payments.landlord.property.nextTerm;
  const listingName = listing.name;
  const moveInCosts = getMoveInCost(moveInCostsSettings);
  return {
    moveInCostsSettings,
    rentTermsKey,
    tenantFirstName,
    listingName,
    moveInCosts,
    identityCheckResponse,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }: any) => ({
  onSubmit: ({ moveInCostsSettings, rentTermsKey, identityCheckResponse }: MoveInCostsOnSubmitInterface) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate }: MoveInCostsSettingsInterface =
          moveInCostsSettings;
        const { proratedRentNotes, proratedRentPaymentAmount }: ProratedRentSettingsInterface = proratedRentSettings;
        const { securityDepositNotes, securityDepositPaymentAmount }: SecurityDepositSettingsInterface =
          securityDepositSettings;

        const moveInCosts: Array<MoveInCostsInterface> = [];
        if (securityDepositSettings.securityDepositStartsOpen === true) {
          moveInCosts.push({
            type: 'deposit',
            amount: securityDepositPaymentAmount,
            notes: securityDepositNotes,
          });
        }
        if (proratedRentSettings.proratedRentStartsOpen === true) {
          moveInCosts.push({
            type: 'prorated-rent',
            amount: proratedRentPaymentAmount,
            notes: proratedRentNotes,
          });
        }
        otherSettings.forEach((settings) => {
          moveInCosts.push({
            type: 'other',
            subType: settings.otherFeeType,
            amount: settings.otherPaymentAmount,
            notes: settings.otherNotes,
          });
        });
        const payload = {
          dueDate,
          moveInCosts,
          rentTermsKey,
          identityCheckResponse,
        };
        if (
          proratedRentSettings.proratedRentStartsOpen === true ||
          securityDepositSettings.securityDepositStartsOpen === true ||
          otherSettings.length !== 0
        ) {
          return dispatch(createMoveInCosts({ propertyId, ...payload }));
        }
        return { success: true };
      })
      .then(checkRequestActionSuccess)
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  goToDashboard: (tenantFirstName: string) =>
    Promise.resolve()
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getIreneDashboard({ propertyId })))
      .then(() =>
        dispatch(
          showPageNotification(MOVE_IN_COSTS_REQUEST_SUCCESS, {
            tenantFirstName,
          }),
        ),
      ),
  goToMoveInCostsModal: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(push(baseUrl + paths.dashboard.createMoveInCostsPage))),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getIreneDashboard({ propertyId }))),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => {
  const { moveInCostsSettings, rentTermsKey, tenantFirstName, identityCheckResponse } = stateProps;
  return {
    ...stateProps,
    ...dispatchProps,
    onSubmit: () =>
      dispatchProps.onSubmit({
        moveInCostsSettings,
        rentTermsKey,
        tenantFirstName,
        identityCheckResponse,
      }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ConfirmMoveInCostsPage);
